/**
 * MineProd chart renderer v1.3.0  ·  draws a mineprod.chart/1.x payload (9 chart types) as an interactive SVG chart.
 * No dependencies. Works in any framework (React wrapper: MineProdChart.js).
 *
 *   const handle = renderMineProdChart(element, chartPayload, { theme: "auto", allowTypeSwitch: true });
 *   handle.update(newPayload);  handle.setTheme("dark");  handle.destroy();
 *   handle.copyImage();  handle.copyData();  handle.downloadPng();  handle.toPngBlob();   (also in the chart's Copy menu)
 *
 * Rules it follows (see README): never computes values (formats only), verifies integrity.checksum before drawing,
 * colours by series role, one value axis, labels inserted with textContent only.
 */
/* global globalThis */

export const RENDERER_VERSION = "1.3.0";
export const SUPPORTED_TYPES = ["line", "bar", "horizontal_bar", "stacked_bar", "combo_bar_line", "waterfall", "donut", "scatter", "heatmap"];
const SPECIAL = ["donut", "scatter", "heatmap"];

const TYPE_LABELS = { line: "Line", bar: "Bar", horizontal_bar: "Horizontal", stacked_bar: "Stacked", combo_bar_line: "Combo", waterfall: "Waterfall", donut: "Donut", scatter: "Scatter", heatmap: "Heatmap" };
const HEIGHTS = { sm: 240, md: 320, lg: 400 };
const STATUS_TEXT = { good: "At or above plan", warning: "Within 3% of plan", critical: "3% or more off plan" };
const STATUS_ICON = { good: "✓", warning: "!", critical: "✕" };
const NS = "http://www.w3.org/2000/svg";

/* ------------------------------------------------------------------------------------------------ styles */
const CSS = `
.mpc{
  --mpc-surface:#fcfcfb;--mpc-raise:#eef1ec;--mpc-fg:#14201a;--mpc-fg-2:#4b5650;--mpc-muted:#7d8780;
  --mpc-grid:#e1e4dd;--mpc-axis:#c3c8bf;--mpc-border:#d8ddd5;--mpc-focus:#a8841a;
  --mpc-actual:#0b5d3b;--mpc-plan:#7d8780;--mpc-pos:#1e8e3e;--mpc-neg:#c62828;--mpc-total:#37474f;--mpc-start:#0b5d3b;
  --mpc-cat1:#2a78d6;--mpc-cat2:#eb6834;--mpc-cat3:#1baf7a;--mpc-cat4:#eda100;--mpc-cat5:#e87ba4;--mpc-cat6:#008300;--mpc-cat7:#6250d6;--mpc-cat8:#e34948;
  --mpc-good:#0ca30c;--mpc-warning:#fab219;--mpc-critical:#d03b3b;--mpc-warning-ink:#3a2a00;
  --mpc-tip-bg:#14201a;--mpc-tip-fg:#f3f5f1;--mpc-tip-2:#b9c2bb;
  --mpc-font:"IBM Plex Sans","Segoe UI",Roboto,system-ui,sans-serif;--mpc-font-title:var(--mpc-font);--mpc-radius:10px;
}
.mpc[data-mpc-theme="dark"]{
  --mpc-surface:#161c18;--mpc-raise:#1d2520;--mpc-fg:#eef2ee;--mpc-fg-2:#b8c2bb;--mpc-muted:#8a948d;
  --mpc-grid:#26302a;--mpc-axis:#3a453e;--mpc-border:#2b352f;--mpc-focus:#d8b44a;
  --mpc-actual:#3fa37a;--mpc-plan:#9aa39c;--mpc-pos:#3fb862;--mpc-neg:#e66767;--mpc-total:#8fa3ad;--mpc-start:#3fa37a;
  --mpc-cat1:#3987e5;--mpc-cat2:#d95926;--mpc-cat3:#199e70;--mpc-cat4:#c98500;--mpc-cat5:#d55181;--mpc-cat6:#008300;--mpc-cat7:#9085e9;--mpc-cat8:#e66767;
  --mpc-tip-bg:#eef2ee;--mpc-tip-fg:#10170f;--mpc-tip-2:#4b5650;color-scheme:dark;
}
@media (prefers-color-scheme:dark){.mpc[data-mpc-theme="auto"]{
  --mpc-surface:#161c18;--mpc-raise:#1d2520;--mpc-fg:#eef2ee;--mpc-fg-2:#b8c2bb;--mpc-muted:#8a948d;
  --mpc-grid:#26302a;--mpc-axis:#3a453e;--mpc-border:#2b352f;--mpc-focus:#d8b44a;
  --mpc-actual:#3fa37a;--mpc-plan:#9aa39c;--mpc-pos:#3fb862;--mpc-neg:#e66767;--mpc-total:#8fa3ad;--mpc-start:#3fa37a;
  --mpc-cat1:#3987e5;--mpc-cat2:#d95926;--mpc-cat3:#199e70;--mpc-cat4:#c98500;--mpc-cat5:#d55181;--mpc-cat6:#008300;--mpc-cat7:#9085e9;--mpc-cat8:#e66767;
  --mpc-tip-bg:#eef2ee;--mpc-tip-fg:#10170f;--mpc-tip-2:#4b5650;color-scheme:dark;}}
.mpc{font-family:var(--mpc-font);color:var(--mpc-fg);font-size:14px;line-height:1.45;box-sizing:border-box;min-width:0}
.mpc *{box-sizing:border-box}
.mpc [hidden]{display:none!important}
.mpc.mpc-card{background:var(--mpc-surface);border:1px solid var(--mpc-border);border-radius:var(--mpc-radius);padding:16px 16px 12px}
.mpc.mpc-plain{background:transparent}
.mpc-inner{display:flex;flex-direction:column;gap:10px}
.mpc-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap}
.mpc-titles{min-width:0}
.mpc-title{font-family:var(--mpc-font-title);font-weight:700;font-size:16px;margin:0;color:var(--mpc-fg)}
.mpc-sub{margin:2px 0 0;color:var(--mpc-muted);font-size:12.5px}
.mpc-tools{display:flex;gap:6px;align-items:center;flex-wrap:wrap;position:relative}
.mpc-copy{position:relative}
.mpc-menu{position:absolute;right:0;top:calc(100% + 4px);z-index:6;min-width:230px;background:var(--mpc-surface);border:1px solid var(--mpc-border);border-radius:8px;padding:4px;box-shadow:0 6px 22px rgba(0,0,0,.16);display:flex;flex-direction:column}
.mpc-menu button{font:500 12.5px var(--mpc-font);text-align:left;background:transparent;border:0;border-radius:5px;color:var(--mpc-fg);padding:7px 10px;cursor:pointer}
.mpc-menu button:hover,.mpc-menu button:focus-visible{background:var(--mpc-raise)}
.mpc-menu small{display:block;color:var(--mpc-muted);font-size:11px;font-weight:400}
.mpc-live{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.mpc-seg{display:inline-flex;border:1px solid var(--mpc-border);border-radius:7px;overflow:hidden}
.mpc-seg button,.mpc-chip{font:500 12px var(--mpc-font);background:transparent;color:var(--mpc-fg-2);border:0;padding:4px 10px;cursor:pointer}
.mpc-seg button+button{border-left:1px solid var(--mpc-border)}
.mpc-seg button[aria-pressed="true"],.mpc-chip[aria-pressed="true"]{background:var(--mpc-raise);color:var(--mpc-fg)}
.mpc-chip{border:1px solid var(--mpc-border);border-radius:7px}
.mpc button:focus-visible,.mpc-plot:focus-visible{outline:2px solid var(--mpc-focus);outline-offset:2px}
.mpc-plot{position:relative;width:100%;touch-action:pan-y;border-radius:4px}
.mpc-plot svg{display:block;width:100%;overflow:visible}
.mpc-plot text{font-family:var(--mpc-font)}
.mpc-legend{display:flex;flex-wrap:wrap;gap:4px 14px;font-size:12.5px;color:var(--mpc-fg-2)}
.mpc-legend button{display:inline-flex;align-items:center;gap:7px;background:none;border:0;color:inherit;font:inherit;cursor:pointer;padding:2px 0}
.mpc-legend button:disabled{cursor:default}
.mpc-legend button[aria-pressed="false"]{opacity:.4;text-decoration:line-through}
.mpc-key{display:inline-block;width:14px;height:10px;border-radius:2px}
.mpc-key.mpc-line{height:0;border-top:2px solid;border-radius:0}
.mpc-key.mpc-dashed{border-top-style:dashed}
.mpc-statuskey{display:flex;flex-wrap:wrap;gap:4px 14px;font-size:12px;color:var(--mpc-muted)}
.mpc-st{display:inline-flex;align-items:center;gap:5px}
.mpc-dot{width:14px;height:14px;border-radius:50%;display:inline-grid;place-items:center;font:700 9px var(--mpc-font);color:#fff;flex:none;font-style:normal}
.mpc-dot.good{background:var(--mpc-good)}.mpc-dot.warning{background:var(--mpc-warning);color:var(--mpc-warning-ink)}.mpc-dot.critical{background:var(--mpc-critical)}
.mpc-tip{position:absolute;pointer-events:none;background:var(--mpc-tip-bg);color:var(--mpc-tip-fg);border-radius:8px;padding:9px 11px;font-size:12.5px;min-width:170px;max-width:260px;box-shadow:0 6px 22px rgba(0,0,0,.18);z-index:5;display:flex;flex-direction:column;gap:4px}
.mpc-tip .mpc-tt{font-weight:600;font-size:12px;color:var(--mpc-tip-2)}
.mpc-tip .mpc-row{display:grid;grid-template-columns:14px 1fr auto;gap:8px;align-items:center}
.mpc-tip .mpc-row b{font-variant-numeric:tabular-nums;font-weight:600}
.mpc-tip .mpc-nm{color:var(--mpc-tip-2)}
.mpc-tip .mpc-row.mpc-em b{font-size:13.5px}
.mpc-tip .mpc-sw{width:12px;height:0;border-top:2px solid}
.mpc-tip .mpc-st{margin-top:2px;color:var(--mpc-tip-2)}
.mpc-tablewrap{overflow-x:auto}
.mpc table{border-collapse:collapse;width:100%;font-size:12.5px;font-variant-numeric:tabular-nums}
.mpc th,.mpc td{text-align:right;padding:6px 10px;border-bottom:1px solid var(--mpc-border);white-space:nowrap;color:var(--mpc-fg)}
.mpc th:first-child,.mpc td:first-child{text-align:left}
.mpc th{font-weight:600;color:var(--mpc-fg-2);font-size:12px}
.mpc-foot{font-size:11.5px;color:var(--mpc-muted)}
.mpc-key.mpc-round{width:10px;height:10px;border-radius:50%}
.mpc-legval{color:var(--mpc-muted);font-variant-numeric:tabular-nums;margin-left:2px}
.mpc-heatkey{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:12px;color:var(--mpc-fg-2)}
.mpc-heatbar{display:inline-flex;height:10px;border-radius:2px;overflow:hidden}
.mpc-heatbar i{display:block;width:18px;height:10px}
.mpc-heatnum{font-variant-numeric:tabular-nums;color:var(--mpc-muted)}
.mpc-nodata{display:inline-block;width:14px;height:10px;border-radius:2px;background:linear-gradient(135deg,transparent 45%,var(--mpc-border) 45%,var(--mpc-border) 55%,transparent 55%),var(--mpc-raise)}
`;

function ensureStyles(doc) {
  if (doc.getElementById("mpc-styles")) return;
  const s = doc.createElement("style");
  s.id = "mpc-styles";
  s.textContent = CSS;
  (doc.head || doc.documentElement).appendChild(s);
}

/* ------------------------------------------------------------------------------------------------ helpers */
export function formatValue(v, f, signed) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v !== "number" || !isFinite(v)) return String(v);
  f = f || { decimals: 0, thousands_separator: true, unit: "", unit_position: "suffix" };
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: f.decimals, maximumFractionDigits: f.decimals, useGrouping: f.thousands_separator !== false });
  const zero = Number(Math.abs(v).toFixed(f.decimals)) === 0;
  const sign = zero ? "" : v < 0 ? "−" : signed && v > 0 ? "+" : "";
  if (!f.unit) return sign + s;
  if (f.unit_position === "prefix") return sign + f.unit + s;
  return sign + s + (f.unit === "%" ? "%" : " " + f.unit);
}

function niceScale(lo, hi, n = 5) {
  if (lo === hi) { lo -= Math.abs(lo) * 0.1 || 1; hi += Math.abs(hi) * 0.1 || 1; }
  const raw = (hi - lo) / n, mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((x) => x >= raw);
  const min = Math.floor(lo / step + 1e-9) * step, max = Math.ceil(hi / step - 1e-9) * step;
  const ticks = [];
  for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
  return { min, max, ticks };
}

const axisLabel = (v, f) => (Math.abs(v) >= 10000 ? (v / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 }) + "k" : formatValue(v, { ...f, unit: "" }));

export function canonicalJson(v) {
  if (Array.isArray(v)) return "[" + v.map(canonicalJson).join(",") + "]";
  if (v && typeof v === "object") return "{" + Object.keys(v).sort().map((k) => JSON.stringify(k) + ":" + canonicalJson(v[k])).join(",") + "}";
  return JSON.stringify(v);
}

export async function verifyChecksum(chart) {
  const c = globalThis.crypto;
  if (!c || !c.subtle) return null;                       // not available (non-secure context): unknown
  const buf = await c.subtle.digest("SHA-256", new TextEncoder().encode(canonicalJson(chart.data)));
  const hex = [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  return "sha256:" + hex === (chart.integrity && chart.integrity.checksum);
}

/** Structural check of a payload. Returns a list of problems (empty = drawable). */
export function validateChart(chart) {
  const p = [];
  if (!chart || typeof chart !== "object") return ["chart is not an object"];
  if (typeof chart.schema_version !== "string" || !/^mineprod\.chart\/1\./.test(chart.schema_version)) p.push("unsupported schema_version " + chart.schema_version);
  if (!SUPPORTED_TYPES.includes(chart.type)) p.push("unsupported type " + chart.type);
  if (!Array.isArray(chart.data) || chart.data.length < 2) p.push("data needs at least 2 rows");
  if (!Array.isArray(chart.series) || !chart.series.length) p.push("series is empty");
  if (!chart.x_axis || !chart.y_axis || !chart.tooltip) p.push("x_axis, y_axis and tooltip are required");
  if (p.length) return p;
  const keys = [chart.x_axis.key, chart.x_axis.label_key, ...chart.series.map((s) => s.key), ...chart.tooltip.rows.map((r) => r.key)];
  chart.data.forEach((row, i) => keys.forEach((k) => { if (!(k in row)) p.push(`data[${i}] is missing "${k}"`); }));
  if (chart.type === "waterfall" && !chart.series[0].waterfall) p.push("waterfall series needs start_key, end_key, kind_key");
  if (chart.type === "donut") {
    const d = chart.donut;
    if (!d || !d.value_key) p.push("donut needs donut.value_key");
    else if (chart.data.some((r) => typeof r[d.value_key] !== "number" || r[d.value_key] < 0)) p.push("donut values must be numbers >= 0");
  }
  if (chart.type === "scatter") {
    if (!chart.scatter) p.push("scatter needs the scatter block");
    if (!chart.x_axis.value_key || !chart.x_axis.format) p.push("scatter needs x_axis.value_key and x_axis.format");
    else chart.data.forEach((r, i) => { if (typeof r[chart.x_axis.value_key] !== "number") p.push(`data[${i}] x value is not a number`); });
  }
  if (chart.type === "heatmap") {
    const hm = chart.heatmap;
    if (!hm || !hm.row_key || !hm.col_key || !hm.value_key || !Array.isArray(hm.rows) || !Array.isArray(hm.cols)) p.push("heatmap needs row_key, col_key, value_key, rows and cols");
    else if (typeof hm.domain_min !== "number" || typeof hm.domain_max !== "number") p.push("heatmap needs domain_min and domain_max");
  }
  return p;
}

/** Chart types the same data can be shown as (first = the payload's own type). Values never change, only the marks. */
export function compatibleTypes(chart) {
  const t = chart.type, s = chart.series || [];
  if (t === "waterfall" || t === "scatter" || t === "heatmap") return [t];
  if (t === "donut") return ["donut", "horizontal_bar"];
  if (t === "horizontal_bar") return ["horizontal_bar", "bar"];
  if (t === "stacked_bar") return ["stacked_bar", "bar"];
  const hasPlan = s.some((x) => x.role === "plan" || x.role === "target");
  const hasActual = s.some((x) => x.role === "actual");
  const list = s.length >= 2 && hasPlan && hasActual ? ["combo_bar_line", "bar", "line"] : ["bar", "line"];
  return [t, ...list.filter((x) => x !== t)];
}

function viewAs(chart, type) {
  if (type === chart.type) return chart;
  if (chart.type === "donut") {                           // same slices as ranked bars
    return { ...chart, type, series: chart.series.map((s) => ({ ...s, mark: "bar" })), y_axis: { ...chart.y_axis, min: 0 },
             data_labels: { mode: "all" }, legend: { show: false, position: "none" }, tooltip: { ...chart.tooltip, mode: "per_mark" } };
  }
  const allNum = chart.data.every((r) => chart.series.every((s) => typeof r[s.key] !== "number" || r[s.key] >= 0));
  const v = { ...chart, type, series: chart.series.map((s) => ({ ...s })), y_axis: { ...chart.y_axis }, data_labels: { ...chart.data_labels } };
  v.series.forEach((s) => {
    if (type === "line") { s.mark = "line"; s.show_points = true; if (s.role !== "plan" && s.role !== "target") delete s.line_style; }
    else if (type === "combo_bar_line") { if (s.role === "plan" || s.role === "target") { s.mark = "line"; s.line_style = "dashed"; s.show_points = true; } else s.mark = "bar"; }
    else s.mark = "bar";
  });
  if (type === "line") { v.y_axis.min = null; if (v.data_labels.mode === "all" || v.data_labels.mode === "totals") v.data_labels.mode = "min_max"; }
  else { v.y_axis.min = allNum ? 0 : null; if (v.data_labels.mode === "min_max" || v.data_labels.mode === "totals") v.data_labels.mode = type === "horizontal_bar" ? "all" : "none"; }
  if (type === "bar" && chart.type === "horizontal_bar") v.data_labels.mode = "none";
  if (type === "horizontal_bar") v.tooltip = { ...chart.tooltip, mode: "per_mark" };
  return v;
}

/* ------------------------------------------------------------------------------------------------ main */
export function renderMineProdChart(container, chart, options = {}) {
  const doc = container.ownerDocument;
  ensureStyles(doc);
  const opts = { theme: "auto", allowTypeSwitch: true, allowCopy: true, showTable: false, verify: true, frame: null, onError: null, onTypeChange: null, onCopy: null, ...options };
  const state = { chart: null, view: null, hidden: new Set(), table: !!opts.showTable, destroyed: false, ro: null, mq: null, token: 0 };

  const root = doc.createElement("div");
  root.className = "mpc";
  root.setAttribute("data-mpc-theme", opts.theme);
  container.replaceChildren(root);

  const h = (tag, cls, text) => { const e = doc.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
  const svgEl = (tag, attrs, parent) => { const e = doc.createElementNS(NS, tag); for (const k in attrs) e.setAttribute(k, attrs[k]); if (parent) parent.appendChild(e); return e; };
  const css = (name) => getComputedStyle(root).getPropertyValue(name).trim();
  let footEl = null;
  const note = (text) => { if (footEl) { footEl.textContent = text; footEl.hidden = false; } };
  const axisTitle = (svg, x, y, text, rotated) => {
    if (!text) return;
    const t = svgEl("text", { x, y, "text-anchor": "middle", "font-size": 11.5, "font-weight": 500, fill: css("--mpc-fg-2") }, svg);
    if (rotated) t.setAttribute("transform", `rotate(-90 ${x} ${y})`);
    t.textContent = text;
  };
  const colorFor = (s) => {
    if (s.role === "plan" || s.role === "target") return css("--mpc-plan");
    if (s.role === "category") return css("--mpc-cat" + Math.min(8, Math.max(1, s.color_index || 1)));
    const actuals = ((state.view || state.chart || {}).series || []).filter((x) => x.role !== "plan" && x.role !== "target" && x.role !== "category");
    if (actuals.length > 1) return css("--mpc-cat" + (Math.max(0, actuals.findIndex((x) => x.key === s.key)) % 8 + 1));   // different metrics -> different colours
    return css("--mpc-actual");
  };
  const fail = (problems) => { root.replaceChildren(); root.hidden = true; if (opts.onError) opts.onError(problems); else console.warn("[mineprod-chart] not drawn:", problems); };

  function build() {
    const c = state.chart;
    if (closeMenu) closeMenu();                       // a redraw replaces the Copy menu: drop the old one's listeners
    root.hidden = false;
    root.className = "mpc " + ((opts.frame || c.layout?.frame) === "none" ? "mpc-plain" : "mpc-card");
    root.replaceChildren();
    const inner = h("div", "mpc-inner"); root.appendChild(inner);
    const head = h("div", "mpc-head"), titles = h("div", "mpc-titles");
    titles.appendChild(h("p", "mpc-title", c.title));
    if (c.subtitle) titles.appendChild(h("p", "mpc-sub", c.subtitle));
    const tools = h("div", "mpc-tools");
    const types = compatibleTypes(c);
    if (opts.allowTypeSwitch && types.length > 1) {
      const seg = h("div", "mpc-seg"); seg.setAttribute("role", "group"); seg.setAttribute("aria-label", "Chart type");
      types.forEach((t) => {
        const label = t === "bar" && types.includes("horizontal_bar") ? "Vertical" : t === "bar" && types.includes("stacked_bar") ? "Grouped" : TYPE_LABELS[t];
        const b = h("button", null, label); b.type = "button"; b.dataset.type = t;
        b.setAttribute("aria-pressed", String(state.view.type === t));
        b.onclick = () => { state.view = viewAs(c, t); state.hidden.clear(); build(); if (opts.onTypeChange) opts.onTypeChange(t); };
        seg.appendChild(b);
      });
      tools.appendChild(seg);
    }
    if (c.interaction?.table_toggle) {
      const tb = h("button", "mpc-chip", "Table"); tb.type = "button"; tb.setAttribute("aria-pressed", String(state.table));
      tb.onclick = () => { state.table = !state.table; build(); };
      tools.appendChild(tb);
    }
    if (opts.allowCopy) tools.appendChild(copyMenu());
    head.append(titles, tools);
    const plot = h("div", "mpc-plot");
    const legend = h("div", "mpc-legend");
    const statusKey = h("div", "mpc-statuskey");
    ["good", "warning", "critical"].forEach((st) => { const sp = h("span", "mpc-st"); sp.append(h("i", "mpc-dot " + st, STATUS_ICON[st]), h("span", null, STATUS_TEXT[st])); statusKey.appendChild(sp); });
    const tableWrap = h("div", "mpc-tablewrap");
    footEl = h("div", "mpc-foot"); footEl.hidden = true;
    inner.append(head, plot, legend, statusKey, footEl, tableWrap);
    draw(plot);
    drawLegend(legend);
    statusKey.hidden = !c.status_legend;
    tableWrap.hidden = !state.table;
    if (state.table) drawTable(tableWrap);
  }

  /* ---------------------------------------------------------------------------- copy: image, data, PNG file */
  let closeMenu = null;
  function copyMenu() {
    const wrap = h("div", "mpc-copy");
    const btn = h("button", "mpc-chip", "Copy"); btn.type = "button";
    btn.setAttribute("aria-haspopup", "menu"); btn.setAttribute("aria-expanded", "false");
    const live = h("span", "mpc-live"); live.setAttribute("aria-live", "polite");
    const menu = h("div", "mpc-menu"); menu.setAttribute("role", "menu"); menu.hidden = true;
    const item = (label, hint, run) => {
      const b = h("button"); b.type = "button"; b.setAttribute("role", "menuitem");
      b.append(doc.createTextNode(label), h("small", null, hint));
      b.onclick = async () => {
        close();
        let result;
        try { result = await run(); } catch (e) { result = "failed"; console.warn("[mineprod-chart] copy failed:", e); }
        const text = { image: "Copied", data: "Copied", saved: "Saved", failed: "Copy failed" }[result] || "Done";
        btn.textContent = text; live.textContent = text === "Saved" ? "Chart saved as a PNG file" : text === "Copied" ? "Copied to the clipboard" : "Copy failed";
        setTimeout(() => { btn.textContent = "Copy"; }, 1600);
        if (opts.onCopy) opts.onCopy(result);
        btn.focus();
      };
      return b;
    };
    menu.append(item("Copy image", "Paste into email, Teams, PowerPoint", copyImage),
                item("Copy data", "Paste into Excel or Word as a table", copyData),
                item("Download PNG", "Save the chart as an image file", downloadPng));
    const onDoc = (e) => { if (!wrap.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === "Escape") { close(); btn.focus(); } };
    function open() { menu.hidden = false; btn.setAttribute("aria-expanded", "true"); doc.addEventListener("pointerdown", onDoc, true); doc.addEventListener("keydown", onKey, true); menu.querySelector("button").focus(); }
    function close() { menu.hidden = true; btn.setAttribute("aria-expanded", "false"); doc.removeEventListener("pointerdown", onDoc, true); doc.removeEventListener("keydown", onKey, true); }
    closeMenu = close;
    btn.onclick = () => (menu.hidden ? open() : close());
    wrap.append(btn, menu, live);
    return wrap;
  }

  // the table behind the chart, as the Table view shows it, with raw numbers (unit in the header) so Excel keeps them as numbers
  function dataMatrix() {
    const c = state.chart;
    const hasUnit = (label, u) => (u === "%" ? String(label).includes("%")
      : new RegExp("(^|[^A-Za-z])" + u.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&") + "([^A-Za-z]|$)").test(String(label)));
    const unitOf = (f, label = "") => (f && f.unit && !hasUnit(label, f.unit) ? ` (${f.unit})` : "");   // "Variance %", not "Variance % (%)"
    if (c.type === "heatmap") {
      const hm = c.heatmap, cell = {};
      c.data.forEach((r) => (cell[r[hm.row_key] + "\u0000" + r[hm.col_key]] = r));
      return [[(hm.row_title || "") + unitOf(c.y_axis.format), ...hm.cols.map((x) => x.label)],
              ...hm.rows.map((rw) => [rw.label, ...hm.cols.map((cl) => { const r = cell[rw.value + "\u0000" + cl.value]; return r && typeof r[hm.value_key] === "number" ? r[hm.value_key] : ""; })])];
    }
    const ss = c.series && c.series.find((x) => x.status_key);
    const withStatus = !!(c.tooltip.show_status && ss);
    const head = [c.x_axis.title || "Item", ...c.tooltip.rows.map((r) => r.label + unitOf(r.format, r.label))];
    if (withStatus) head.push("Status");
    const rows = c.data.map((r) => {
      const line = [String(r[c.tooltip.title_key] ?? r[c.x_axis.label_key] ?? "")];
      c.tooltip.rows.forEach((x) => line.push(typeof r[x.key] === "number" ? r[x.key] : ""));
      if (withStatus) line.push(STATUS_TEXT[r[ss.status_key]] || "");
      return line;
    });
    return [head, ...rows];
  }

  async function copyData() {
    const m = dataMatrix(), c = state.chart;
    const cellText = (v) => String(v).replace(/[\t\r\n]+/g, " ");
    const tsv = m.map((r) => r.map(cellText).join("\t")).join("\n");
    const esc = (v) => String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const html = `<table><caption>${esc(c.title)}${c.subtitle ? " · " + esc(c.subtitle) : ""}</caption><thead><tr>${m[0].map((x) => `<th>${esc(x)}</th>`).join("")}</tr></thead><tbody>`
      + m.slice(1).map((r) => `<tr>${r.map((x) => `<td>${esc(x)}</td>`).join("")}</tr>`).join("") + "</tbody></table>";
    const nav = globalThis.navigator;
    if (nav && nav.clipboard && typeof nav.clipboard.write === "function" && typeof globalThis.ClipboardItem === "function") {
      try {
        await nav.clipboard.write([new ClipboardItem({ "text/plain": new Blob([tsv], { type: "text/plain" }), "text/html": new Blob([html], { type: "text/html" }) })]);
        return "data";
      } catch (e) { /* fall through to plain text */ }
    }
    if (nav && nav.clipboard && typeof nav.clipboard.writeText === "function") {
      try { await nav.clipboard.writeText(tsv); return "data"; } catch (e) { /* fall through */ }
    }
    const ta = h("textarea"); ta.value = tsv; ta.setAttribute("readonly", ""); ta.style.cssText = "position:fixed;left:-9999px;top:0";
    doc.body.appendChild(ta); ta.select();
    const ok = doc.execCommand && doc.execCommand("copy"); ta.remove();
    return ok ? "data" : "failed";
  }

  // one PNG of the whole card: title, subtitle, plot, legend, status key and note (drawn at 2x for sharp pasting)
  async function toPngBlob() {
    const svg = root.querySelector(".mpc-plot svg");
    if (!svg) throw new Error("nothing drawn");
    const rect = svg.getBoundingClientRect();
    const W = Math.max(320, Math.round(rect.width)), H = Math.max(120, Math.round(rect.height));
    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", NS); clone.setAttribute("width", W); clone.setAttribute("height", H);
    if (!clone.getAttribute("viewBox")) clone.setAttribute("viewBox", `0 0 ${W} ${H}`);
    const font = css("--mpc-font") || "sans-serif";
    const st = doc.createElementNS(NS, "style"); st.textContent = `text{font-family:${font}}`; clone.insertBefore(st, clone.firstChild);
    const xml = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    img.decoding = "sync";
    await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error("svg image failed")); img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml); });

    const pad = 20, scale = 2, cw = W + pad * 2;
    const meas = doc.createElement("canvas").getContext("2d");
    const wrapText = (text, f, maxW) => {
      meas.font = f; const words = String(text || "").split(/\s+/).filter(Boolean), lines = []; let cur = "";
      words.forEach((w) => { const t = cur ? cur + " " + w : w; if (meas.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t; });
      if (cur) lines.push(cur); return lines;
    };
    const fTitle = `700 16px ${font}`, fSub = `400 12.5px ${font}`, fLeg = `400 12.5px ${font}`, fFoot = `400 11.5px ${font}`;
    const c = state.chart;
    const titleLines = wrapText(c.title, fTitle, W), subLines = c.subtitle ? wrapText(c.subtitle, fSub, W) : [];

    // legend items from what is on screen (so it matches the chart exactly)
    const items = [];
    const legend = root.querySelector(".mpc-legend");
    if (legend && !legend.hidden) {
      legend.querySelectorAll(":scope > button").forEach((b) => {
        if (b.getAttribute("aria-pressed") === "false") return;
        const k = b.querySelector(".mpc-key"); if (!k) return;
        const cs = getComputedStyle(k);
        const line = k.classList.contains("mpc-line");
        items.push({ kind: line ? "line" : k.classList.contains("mpc-round") ? "dot" : "box", dashed: k.classList.contains("mpc-dashed"),
                     color: line ? cs.borderTopColor : cs.backgroundColor,
                     text: Array.from(b.childNodes).map((n) => n.textContent.trim()).filter(Boolean).join(" ") });   // "Waste 55.6%"
      });
      legend.querySelectorAll(".mpc-heatkey > *").forEach((el) => {
        if (el.classList.contains("mpc-heatbar")) { el.querySelectorAll("i").forEach((i) => items.push({ kind: "ramp", color: getComputedStyle(i).backgroundColor })); items.push({ kind: "gap" }); }
        else if (el.classList.contains("mpc-st")) items.push({ kind: "box", color: css("--mpc-raise"), text: el.textContent });
        else items.push({ kind: "text", text: el.textContent });
      });
    }
    const statusKey = root.querySelector(".mpc-statuskey");
    if (statusKey && !statusKey.hidden) ["good", "warning", "critical"].forEach((k) => items.push({ kind: "status", status: k, text: STATUS_TEXT[k] }));
    meas.font = fLeg;
    const widthOf = (it) => it.kind === "gap" ? 8 : it.kind === "ramp" ? 18 : it.kind === "text" ? meas.measureText(it.text).width + 8 : 22 + meas.measureText(it.text || "").width + 14;
    const rowsL = []; let row = [], used = 0;
    items.forEach((it) => { const w = widthOf(it); if (used + w > W && row.length) { rowsL.push(row); row = []; used = 0; } row.push([it, w]); used += w; });
    if (row.length) rowsL.push(row);
    const foot = root.querySelector(".mpc-foot");
    const footLines = foot && !foot.hidden && foot.textContent ? wrapText(foot.textContent, fFoot, W) : [];

    const hTitle = titleLines.length * 22, hSub = subLines.length * 18, hLeg = rowsL.length * 22, hFoot = footLines.length * 16;
    const ch = pad + hTitle + hSub + 12 + H + (hLeg ? 12 + hLeg : 0) + (hFoot ? 8 + hFoot : 0) + pad;
    const canvas = doc.createElement("canvas");
    canvas.width = cw * scale; canvas.height = Math.round(ch * scale);
    const g = canvas.getContext("2d"); g.scale(scale, scale);
    g.fillStyle = css("--mpc-surface") || "#ffffff"; g.fillRect(0, 0, cw, ch);
    let y = pad; g.textBaseline = "top";
    g.fillStyle = css("--mpc-fg"); g.font = fTitle; titleLines.forEach((l) => { g.fillText(l, pad, y); y += 22; });
    g.fillStyle = css("--mpc-muted"); g.font = fSub; subLines.forEach((l) => { g.fillText(l, pad, y + 1); y += 18; });
    y += 12; g.drawImage(img, pad, y, W, H); y += H;
    if (hLeg) {
      y += 12; g.font = fLeg; g.textBaseline = "middle";
      rowsL.forEach((r) => {
        let x = pad; const cy = y + 11;
        r.forEach(([it, w]) => {
          if (it.kind === "gap") { /* space after the colour ramp */ }
          else if (it.kind === "ramp") { g.fillStyle = it.color; g.fillRect(x, cy - 5, 18, 10); }
          else if (it.kind === "text") { g.fillStyle = css("--mpc-fg-2"); g.fillText(it.text, x, cy); }
          else {
            if (it.kind === "line") { g.strokeStyle = it.color; g.lineWidth = 2; g.setLineDash(it.dashed ? [4, 3] : []); g.beginPath(); g.moveTo(x, cy); g.lineTo(x + 14, cy); g.stroke(); g.setLineDash([]); }
            else if (it.kind === "dot" || it.kind === "status") { g.fillStyle = it.kind === "status" ? css("--mpc-" + it.status) : it.color; g.beginPath(); g.arc(x + 7, cy, it.kind === "status" ? 7 : 5, 0, Math.PI * 2); g.fill(); }
            else { g.fillStyle = it.color; g.fillRect(x, cy - 5, 14, 10); }
            if (it.kind === "status") { g.fillStyle = it.status === "warning" ? css("--mpc-warning-ink") : "#ffffff"; g.font = `700 9px ${font}`; g.textAlign = "center"; g.fillText(STATUS_ICON[it.status], x + 7, cy + 0.5); g.textAlign = "left"; g.font = fLeg; }
            g.fillStyle = css("--mpc-fg-2"); g.fillText(it.text || "", x + 22, cy);
          }
          x += w;
        });
        y += 22;
      });
    }
    if (hFoot) { y += 8; g.textBaseline = "top"; g.fillStyle = css("--mpc-muted"); g.font = fFoot; footLines.forEach((l) => { g.fillText(l, pad, y); y += 16; }); }
    return await new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("png failed"))), "image/png"));
  }

  const fileName = () => (String(state.chart.title || "chart").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "chart") + ".png";
  async function downloadPng() {
    const blob = await toPngBlob();
    const url = URL.createObjectURL(blob), a = doc.createElement("a");
    a.href = url; a.download = fileName(); doc.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "saved";
  }
  async function copyImage() {
    const nav = globalThis.navigator;
    if (nav && nav.clipboard && typeof nav.clipboard.write === "function" && typeof globalThis.ClipboardItem === "function") {
      try {
        await nav.clipboard.write([new ClipboardItem({ "image/png": toPngBlob() })]);   // a promise keeps Safari's user-gesture rule
        return "image";
      } catch (e) { console.warn("[mineprod-chart] image copy not allowed here, saving a PNG instead:", e && e.message); }
    }
    return downloadPng();                                                            // browsers without image clipboard: save the file
  }

  function draw(plot) {
    const c = state.view;
    plot.replaceChildren();
    plot.setAttribute("role", "img");
    plot.setAttribute("aria-label", (c.accessibility && c.accessibility.summary) || c.title);
    if (SPECIAL.includes(c.type)) return drawSpecial(plot);
    const W = Math.max(280, plot.clientWidth || container.clientWidth || 600);
    const H = HEIGHTS[c.layout && c.layout.height] || 320;
    const horiz = c.type === "horizontal_bar";
    const rows = c.data, n = rows.length, lk = c.x_axis.label_key, fy = c.y_axis.format;
    const series = c.series.filter((s) => !state.hidden.has(s.key));
    const longestCat = Math.max(...rows.map((r) => String(r[lk]).length));
    const xTitle = c.type === "waterfall" ? "" : (horiz ? "" : (c.x_axis.title || ""));
    const m = { t: 16, r: horiz ? 60 : 14, b: (c.type === "waterfall" ? 46 : 36) + (horiz || xTitle ? 18 : 0), l: horiz ? Math.min(140, 16 + longestCat * 7) : 72 };
    const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, height: H }, plot);
    const pw = W - m.l - m.r, ph = H - m.t - m.b;

    // value domain (from the payload's own numbers; nothing is recalculated)
    let vals = [];
    if (c.type === "waterfall") { const wf = c.series[0].waterfall; rows.forEach((r) => (r[wf.kind_key] === "total" ? vals.push(r[wf.end_key]) : vals.push(r[wf.start_key], r[wf.end_key]))); }
    else if (c.type === "stacked_bar") rows.forEach((r) => vals.push(series.reduce((a, s) => a + (typeof r[s.key] === "number" ? r[s.key] : 0), 0)));
    else rows.forEach((r) => series.forEach((s) => vals.push(r[s.key])));
    (c.reference_lines || []).forEach((l) => vals.push(l.value));
    vals = vals.filter((v) => typeof v === "number" && isFinite(v));
    if (!vals.length) vals = [0, 1];
    let lo = Math.min(...vals), hi = Math.max(...vals);
    if (c.y_axis.min !== null && c.y_axis.min !== undefined) lo = Math.min(lo, c.y_axis.min);
    else { const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.05 || 1; lo -= pad; hi += pad * 0.4; }
    if (c.y_axis.max !== null && c.y_axis.max !== undefined) hi = Math.max(c.y_axis.max, lo + 1e-9);
    const sc = niceScale(lo, hi);
    if (c.y_axis.min !== null && c.y_axis.min !== undefined) sc.min = c.y_axis.min;
    sc.ticks = sc.ticks.filter((t) => t >= sc.min - 1e-9 && t <= sc.max + 1e-9);
    const V = (v) => (horiz ? m.l + ((v - sc.min) / (sc.max - sc.min)) * pw : m.t + ph - ((v - sc.min) / (sc.max - sc.min)) * ph);
    const slot = (horiz ? ph : pw) / n;
    const C = (i) => (horiz ? m.t : m.l) + slot * (i + 0.5);
    const gridC = css("--mpc-grid"), mutedC = css("--mpc-muted"), axisC = css("--mpc-axis"), fgC = css("--mpc-fg"), fg2 = css("--mpc-fg-2"), surf = css("--mpc-surface");

    // grid + value axis
    sc.ticks.forEach((t) => {
      if (horiz) {
        if (c.layout?.show_grid !== false) svgEl("line", { x1: V(t), x2: V(t), y1: m.t, y2: m.t + ph, stroke: gridC }, svg);
        svgEl("text", { x: V(t), y: m.t + ph + 16, "text-anchor": "middle", "font-size": 11, fill: mutedC }, svg).textContent = axisLabel(t, fy);
      } else {
        if (c.layout?.show_grid !== false) svgEl("line", { x1: m.l, x2: m.l + pw, y1: V(t), y2: V(t), stroke: gridC }, svg);
        svgEl("text", { x: m.l - 8, y: V(t) + 4, "text-anchor": "end", "font-size": 11, fill: mutedC }, svg).textContent = axisLabel(t, fy);
      }
    });
    const baseV = V(Math.max(sc.min, Math.min(0, sc.max)));
    if (horiz) svgEl("line", { x1: V(sc.min), x2: V(sc.min), y1: m.t, y2: m.t + ph, stroke: axisC }, svg);
    else svgEl("line", { x1: m.l, x2: m.l + pw, y1: m.t + ph, y2: m.t + ph, stroke: axisC }, svg);
    if (sc.min < 0 && sc.max > 0) {
      if (horiz) svgEl("line", { x1: V(0), x2: V(0), y1: m.t, y2: m.t + ph, stroke: axisC }, svg);
      else svgEl("line", { x1: m.l, x2: m.l + pw, y1: V(0), y2: V(0), stroke: axisC }, svg);
    }
    const yTitle = c.y_axis.title + (fy && fy.unit ? ` (${fy.unit})` : "");
    if (horiz) axisTitle(svg, m.l + pw / 2, H - 4, yTitle, false);                 // value axis: centred under its ticks
    else axisTitle(svg, 13, m.t + ph / 2, yTitle, true);                             // value axis: rotated, centred beside its ticks
    if (xTitle) axisTitle(svg, m.l + pw / 2, H - 4, xTitle, false);                  // category axis: centred under its labels
    if (!horiz && sc.min > 0) note(`Vertical axis starts at ${axisLabel(sc.min, fy)}${fy && fy.unit ? " " + fy.unit : ""}, not zero.`);

    // category labels (thinned so they never collide)
    const longest = Math.max(...rows.map((r) => (c.type === "waterfall" ? Math.max(...String(r[lk]).split(" ").map((w) => w.length), Math.ceil(String(r[lk]).length / 2)) : String(r[lk]).length)));
    const every = horiz ? 1 : Math.max(1, Math.ceil(n / Math.max(1, Math.floor(pw / (longest * 6.4 + 12)))));
    rows.forEach((r, i) => {
      if (i % every) return;
      const label = String(r[lk]);
      if (horiz) { svgEl("text", { x: m.l - 8, y: C(i) + 4, "text-anchor": "end", "font-size": 12, fill: fg2 }, svg).textContent = label; return; }
      const words = label.split(" ");
      const lines = c.type === "waterfall" && words.length > 1 ? [words.slice(0, Math.ceil(words.length / 2)).join(" "), words.slice(Math.ceil(words.length / 2)).join(" ")] : [label];
      lines.forEach((ln, j) => (svgEl("text", { x: C(i), y: m.t + ph + 16 + j * 13, "text-anchor": "middle", "font-size": 11, fill: fg2 }, svg).textContent = ln));
    });

    // reference lines
    (c.reference_lines || []).forEach((l) => {
      const dash = l.line_style === "dashed" ? "5 4" : l.line_style === "dotted" ? "2 3" : "";
      if (horiz) {
        svgEl("line", { x1: V(l.value), x2: V(l.value), y1: m.t, y2: m.t + ph, stroke: css("--mpc-plan"), "stroke-width": 1.5, "stroke-dasharray": dash }, svg);
        svgEl("text", { x: V(l.value) + 4, y: m.t - 6, "font-size": 11, fill: fg2 }, svg).textContent = l.label;
      } else {
        svgEl("line", { x1: m.l, x2: m.l + pw, y1: V(l.value), y2: V(l.value), stroke: css("--mpc-plan"), "stroke-width": 1.5, "stroke-dasharray": dash }, svg);
        svgEl("text", { x: m.l + pw, y: V(l.value) - 6, "text-anchor": "end", "font-size": 11, fill: fg2 }, svg).textContent = l.label;
      }
    });

    // marks
    const marks = [];
    const R = 3;
    const barSeries = series.filter((s) => s.mark === "bar"), lineSeries = series.filter((s) => s.mark !== "bar");
    const single = c.type === "stacked_bar" || c.type === "waterfall";
    const bw = Math.min(46, (slot * 0.62) / Math.max(1, single ? 1 : barSeries.length));
    const barPath = (x, y0, y1, w, horizBar) => {           // rounded at the data end, square at the baseline
      if (horizBar) {
        const a = Math.min(y0, y1), b = Math.max(y0, y1), r = Math.min(R, (b - a) / 2, w / 2), right = y1 >= y0;
        return right ? `M${a},${x}h${b - a - r}a${r},${r} 0 0 1 ${r},${r}v${w - 2 * r}a${r},${r} 0 0 1 -${r},${r}h-${b - a - r}z`
                     : `M${b},${x}h-${b - a - r}a${r},${r} 0 0 0 -${r},${r}v${w - 2 * r}a${r},${r} 0 0 0 ${r},${r}h${b - a - r}z`;
      }
      const top = Math.min(y0, y1), bot = Math.max(y0, y1), hgt = bot - top, r = Math.min(R, hgt / 2, w / 2), up = y1 <= y0;
      return up ? `M${x},${bot}v-${hgt - r}a${r},${r} 0 0 1 ${r},-${r}h${w - 2 * r}a${r},${r} 0 0 1 ${r},${r}v${hgt - r}z`
                : `M${x},${top}v${hgt - r}a${r},${r} 0 0 0 ${r},${r}h${w - 2 * r}a${r},${r} 0 0 0 ${r},-${r}v-${hgt - r}z`;
    };
    const labels = c.data_labels ? c.data_labels.mode : "none";
    const statusChip = (cx, cy, st) => {
      svgEl("circle", { cx, cy, r: 7, fill: css("--mpc-" + st), stroke: surf, "stroke-width": 1.5 }, svg);
      svgEl("text", { x: cx, y: cy + 3.5, "text-anchor": "middle", "font-size": 9, "font-weight": 700, fill: st === "warning" ? css("--mpc-warning-ink") : "#fff" }, svg).textContent = STATUS_ICON[st];
    };

    if (c.type === "waterfall") {
      const s = c.series[0], wf = s.waterfall;
      rows.forEach((r, i) => {
        const kind = r[wf.kind_key], y0 = V(r[wf.start_key]), y1 = V(r[wf.end_key]);
        const col = kind === "total" ? (i === 0 ? css("--mpc-start") : css("--mpc-total")) : kind === "increase" ? css("--mpc-pos") : css("--mpc-neg");
        const p = svgEl("path", { d: barPath(C(i) - bw / 2, kind === "total" ? V(sc.min) : y0, y1, bw), fill: col, stroke: surf, "stroke-width": 1 }, svg);
        marks.push({ i, shape: p });
        if (i < n - 1) svgEl("line", { x1: C(i) + bw / 2, x2: C(i + 1) - bw / 2, y1, y2: y1, stroke: mutedC, "stroke-width": 1 }, svg);
        if (labels === "all" && bw > 20) {
          const up = kind === "total" || r[s.key] >= 0;
          svgEl("text", { x: C(i), y: up ? Math.min(y0, y1) - 5 : Math.max(y0, y1) + 13, "text-anchor": "middle", "font-size": 10.5, "font-weight": 600, fill: fgC }, svg)
            .textContent = formatValue(r[s.key], { ...fy, unit: "" }, kind !== "total");
        }
      });
    } else if (c.type === "stacked_bar") {
      rows.forEach((r, i) => {
        let acc = 0;
        const segs = series.filter((s) => typeof r[s.key] === "number" && r[s.key] > 0);
        segs.forEach((s, j) => {
          const a = acc, b = acc + r[s.key]; acc = b;
          const y0 = V(a), y1 = V(b);
          const shape = j === segs.length - 1
            ? svgEl("path", { d: barPath(C(i) - bw / 2, y0, y1, bw), fill: colorFor(s) }, svg)
            : svgEl("rect", { x: C(i) - bw / 2, y: y1, width: bw, height: Math.max(0, y0 - y1 - 1.5), fill: colorFor(s) }, svg);
          marks.push({ i, shape });
        });
        if (labels === "totals" && segs.length) {
          const tk = c.tooltip.rows.find((t) => t.emphasis);
          const val = tk && state.hidden.size === 0 ? r[tk.key] : acc;   // the payload total when every series is shown
          svgEl("text", { x: C(i), y: V(acc) - 6, "text-anchor": "middle", "font-size": 11, "font-weight": 600, fill: fgC }, svg).textContent = formatValue(val, { ...fy, unit: "" });
        }
      });
    } else {
      barSeries.forEach((s, k) => rows.forEach((r, i) => {
        const v = r[s.key];
        if (typeof v !== "number") return;
        const off = (k - (barSeries.length - 1) / 2) * bw;
        const p = svgEl("path", { d: horiz ? barPath(C(i) - bw / 2 + off, V(Math.max(0, sc.min)), V(v), bw, true) : barPath(C(i) - bw / 2 + off, baseV, V(v), bw), fill: colorFor(s) }, svg);
        marks.push({ i, shape: p });
        if (labels === "all") {
          if (horiz) svgEl("text", { x: V(v) + 6, y: C(i) + 4, "font-size": 11, "font-weight": 600, fill: fgC }, svg).textContent = formatValue(v, fy);
          else svgEl("text", { x: C(i) + off, y: V(v) - 6, "text-anchor": "middle", "font-size": 11, "font-weight": 600, fill: fgC }, svg).textContent = formatValue(v, { ...fy, unit: "" });
        }
        if (s.status_key && r[s.status_key] && STATUS_ICON[r[s.status_key]]) {
          if (horiz) statusChip(V(v) + (labels === "all" ? 54 : 12), C(i) + off, r[s.status_key]);
          else {
            const top = Math.max(v, ...series.filter((x) => x !== s).map((x) => (typeof r[x.key] === "number" ? r[x.key] : -Infinity)));
            statusChip(C(i) + off, V(top) - 12, r[s.status_key]);
          }
        }
      }));
      lineSeries.forEach((s) => {
        const pts = rows.map((r, i) => (typeof r[s.key] === "number" ? [C(i), V(r[s.key])] : null)).filter(Boolean);
        svgEl("polyline", { points: pts.map((p) => p.join(",")).join(" "), fill: "none", stroke: colorFor(s), "stroke-width": 2, "stroke-linejoin": "round",
          "stroke-dasharray": s.line_style === "dashed" ? "6 4" : s.line_style === "dotted" ? "2 3" : "" }, svg);
        if (s.show_points) rows.forEach((r, i) => {
          if (typeof r[s.key] !== "number") return;
          const st = s.status_key && STATUS_ICON[r[s.status_key]] ? r[s.status_key] : null;
          svgEl("circle", { cx: C(i), cy: V(r[s.key]), r: st ? 4.5 : 3.5, fill: st ? css("--mpc-" + st) : colorFor(s), stroke: surf, "stroke-width": 2 }, svg);
        });
        if (labels === "min_max" || labels === "last") {
          const vs = rows.map((r) => r[s.key]).filter((v) => typeof v === "number");
          const pick = labels === "last" ? [[rows.length - 1, -10]] : [[vs.indexOf(Math.max(...vs)), -10], [vs.indexOf(Math.min(...vs)), 18]];
          pick.forEach(([i, dy]) => (svgEl("text", { x: C(i), y: V(rows[i][s.key]) + dy, "text-anchor": "middle", "font-size": 11, "font-weight": 600, fill: fgC }, svg).textContent = formatValue(rows[i][s.key], fy)));
        }
      });
    }

    // hover / focus / tap layer
    const tip = h("div", "mpc-tip"); tip.hidden = true; plot.appendChild(tip);
    const band = svgEl("rect", { fill: fgC, opacity: 0, "pointer-events": "none" }); svg.insertBefore(band, svg.firstChild);
    const hair = svgEl("line", { stroke: mutedC, "stroke-width": 1, opacity: 0, "pointer-events": "none" }, svg);
    const showTip = (i, px, py) => {
      const r = rows[i];
      tip.replaceChildren(h("div", "mpc-tt", String(r[c.tooltip.title_key])));
      c.tooltip.rows.forEach((t) => {
        if (state.hidden.has(t.key)) return;
        const s = c.series.find((x) => x.key === t.key);
        const row = h("div", "mpc-row" + (t.emphasis ? " mpc-em" : ""));
        const sw = h("span", "mpc-sw");
        sw.style.borderTopColor = s && c.type !== "waterfall" ? colorFor(s) : "transparent";
        if (s && s.line_style === "dashed") sw.style.borderTopStyle = "dashed";
        const signed = t.signed || (t.signed_except_kind && r.kind !== t.signed_except_kind);
        row.append(sw, h("span", "mpc-nm", t.label), h("b", null, formatValue(r[t.key], t.format, signed)));
        tip.appendChild(row);
      });
      const ss = c.series.find((s) => s.status_key && STATUS_ICON[r[s.status_key]]);
      if (c.tooltip.show_status && ss) { const st = r[ss.status_key]; const d = h("div", "mpc-st"); d.append(h("i", "mpc-dot " + st, STATUS_ICON[st]), h("span", null, STATUS_TEXT[st])); tip.appendChild(d); }
      tip.hidden = false;
      const pr = plot.getBoundingClientRect(), tw = tip.offsetWidth, th = tip.offsetHeight;
      let x = px + 14, y = py - th - 10;
      if (x + tw > pr.width) x = px - tw - 14;
      if (x < 0) x = 4;
      if (y < 0) y = py + 14;
      tip.style.left = x + "px"; tip.style.top = y + "px";
    };
    const hide = () => { tip.hidden = true; band.setAttribute("opacity", 0); hair.setAttribute("opacity", 0); marks.forEach((mk) => mk.shape.removeAttribute("opacity")); };
    const hit = (clientX, clientY) => {
      const sr = svg.getBoundingClientRect(), k = W / sr.width;
      const px = (clientX - sr.left) * k, py = (clientY - sr.top) * k;
      const pos = horiz ? py : px;
      const i = Math.max(0, Math.min(n - 1, Math.floor((pos - (horiz ? m.t : m.l)) / slot)));
      const inside = horiz ? px >= 0 && py >= m.t && py <= m.t + ph : px >= m.l && px <= m.l + pw && py >= m.t - 12 && py <= m.t + ph + 30;
      const pr = plot.getBoundingClientRect();
      return { i, inside, x: clientX - pr.left, y: clientY - pr.top };
    };
    const focusIndex = (i, x, y) => {
      if (c.tooltip.mode === "shared_x") {
        band.setAttribute("x", horiz ? m.l : C(i) - slot / 2); band.setAttribute("y", horiz ? C(i) - slot / 2 : m.t);
        band.setAttribute("width", horiz ? pw : slot); band.setAttribute("height", horiz ? slot : ph); band.setAttribute("opacity", 0.05);
        if (c.interaction?.crosshair && !horiz) { hair.setAttribute("x1", C(i)); hair.setAttribute("x2", C(i)); hair.setAttribute("y1", m.t); hair.setAttribute("y2", m.t + ph); hair.setAttribute("opacity", 1); }
      }
      marks.forEach((mk) => (mk.i === i ? mk.shape.removeAttribute("opacity") : mk.shape.setAttribute("opacity", 0.45)));
      showTip(i, x, y);
    };
    const move = (e) => { const a = hit(e.clientX, e.clientY); if (!a.inside) return hide(); focusIndex(a.i, a.x, a.y); };
    if (!c.interaction || c.interaction.hover !== false) {
      svg.addEventListener("pointermove", move);
      svg.addEventListener("pointerdown", move);
      svg.addEventListener("pointerleave", hide);
      plot.tabIndex = 0;
      let ki = -1;
      plot.addEventListener("keydown", (e) => {
        if (!["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Escape", "Home", "End"].includes(e.key)) return;
        e.preventDefault();
        if (e.key === "Escape") { ki = -1; return hide(); }
        ki = e.key === "Home" ? 0 : e.key === "End" ? n - 1 : Math.max(0, Math.min(n - 1, ki + (e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1)));
        const sr = svg.getBoundingClientRect(), pr = plot.getBoundingClientRect(), k = sr.width / W;
        const cx = (horiz ? m.l + pw * 0.6 : C(ki)) * k + (sr.left - pr.left), cy = (horiz ? C(ki) : m.t + ph * 0.45) * k + (sr.top - pr.top);
        focusIndex(ki, cx, cy);
      });
      plot.addEventListener("blur", hide);
    }
  }

  function drawLegend(legend) {
    const c = state.view;
    if (SPECIAL.includes(c.type)) return drawSpecialLegend(legend);
    legend.replaceChildren();
    legend.hidden = !(c.legend && c.legend.show) || c.series.length < 2;
    if (legend.hidden) return;
    c.series.forEach((s) => {
      const b = h("button"); b.type = "button";
      b.setAttribute("aria-pressed", String(!state.hidden.has(s.key)));
      const k = h("span", "mpc-key" + (s.mark === "bar" ? "" : " mpc-line") + (s.line_style === "dashed" ? " mpc-dashed" : ""));
      if (s.mark === "bar") k.style.background = colorFor(s); else k.style.borderTopColor = colorFor(s);
      b.append(k, doc.createTextNode(s.name));
      if (c.interaction && c.interaction.series_toggle) {
        b.onclick = () => {
          if (state.hidden.has(s.key)) state.hidden.delete(s.key);
          else if (c.series.length - state.hidden.size > 1) state.hidden.add(s.key);
          build();
        };
      } else b.disabled = true;
      legend.appendChild(b);
    });
  }

  function drawTable(wrap) {
    const c = state.chart;
    if (c.type === "heatmap") return drawHeatTable(wrap);
    const t = h("table"), head = t.createTHead().insertRow(), body = t.createTBody();
    [c.x_axis.title || "Item", ...c.tooltip.rows.map((r) => r.label)].forEach((x) => head.appendChild(h("th", null, x)));
    const ss = c.series.find((s) => s.status_key);
    if (c.tooltip.show_status && ss) head.appendChild(h("th", null, "Status"));
    c.data.forEach((r) => {
      const tr = body.insertRow();
      tr.appendChild(h("td", null, String(r[c.x_axis.label_key])));
      c.tooltip.rows.forEach((x) => tr.appendChild(h("td", null, formatValue(r[x.key], x.format, x.signed || (x.signed_except_kind && r.kind !== x.signed_except_kind)))));
      if (c.tooltip.show_status && ss) {
        const td = h("td"), st = r[ss.status_key];
        if (STATUS_ICON[st]) { const w = h("span", "mpc-st"); w.append(h("i", "mpc-dot " + st, STATUS_ICON[st]), h("span", null, STATUS_TEXT[st])); td.appendChild(w); }
        else td.textContent = "—";
        tr.appendChild(td);
      }
    });
    wrap.replaceChildren(t);
  }

  /* ---------------------------------------------------------------------------- donut, scatter, heatmap */
  function makeTip(plot, c) {
    const tip = h("div", "mpc-tip"); tip.hidden = true; plot.appendChild(tip);
    return {
      show(r, px, py, swatch) {
        tip.replaceChildren(h("div", "mpc-tt", String(r[c.tooltip.title_key])));
        c.tooltip.rows.forEach((t, j) => {
          const row = h("div", "mpc-row" + (t.emphasis ? " mpc-em" : ""));
          const sw = h("span", "mpc-sw"); sw.style.borderTopColor = j === 0 && swatch ? swatch : "transparent";
          row.append(sw, h("span", "mpc-nm", t.label), h("b", null, formatValue(r[t.key], t.format, t.signed)));
          tip.appendChild(row);
        });
        const ss = c.series.find((s) => s.status_key && STATUS_ICON[r[s.status_key]]);
        if (c.tooltip.show_status && ss) { const st = r[ss.status_key]; const d = h("div", "mpc-st"); d.append(h("i", "mpc-dot " + st, STATUS_ICON[st]), h("span", null, STATUS_TEXT[st])); tip.appendChild(d); }
        tip.hidden = false;
        const pr = plot.getBoundingClientRect(), tw = tip.offsetWidth, th = tip.offsetHeight;
        let x = px + 14, y = py - th - 10;
        if (x + tw > pr.width) x = px - tw - 14;
        if (x < 0) x = 4;
        if (y < 0) y = py + 14;
        tip.style.left = x + "px"; tip.style.top = y + "px";
      },
      hide() { tip.hidden = true; },
    };
  }

  // keyboard + pointer wiring shared by the three special types: items = [{i, x, y, el?}] in svg units
  function wireItems(plot, svg, W, items, onFocus, onBlur, pick) {
    const toClient = (it) => { const sr = svg.getBoundingClientRect(), pr = plot.getBoundingClientRect(), k = sr.width / W; return [it.x * k + sr.left - pr.left, it.y * k + sr.top - pr.top]; };
    const onPointer = (e) => {
      const sr = svg.getBoundingClientRect(), k = W / sr.width;
      const it = pick((e.clientX - sr.left) * k, (e.clientY - sr.top) * k);
      if (!it) return onBlur();
      const pr = plot.getBoundingClientRect();
      onFocus(it, e.clientX - pr.left, e.clientY - pr.top);
    };
    svg.addEventListener("pointermove", onPointer);
    svg.addEventListener("pointerdown", onPointer);
    svg.addEventListener("pointerleave", onBlur);
    plot.tabIndex = 0;
    let ki = -1;
    plot.addEventListener("keydown", (e) => {
      if (!["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Escape", "Home", "End"].includes(e.key)) return;
      e.preventDefault();
      if (e.key === "Escape") { ki = -1; return onBlur(); }
      ki = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : Math.max(0, Math.min(items.length - 1, ki + (e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1)));
      const [x, y] = toClient(items[ki]);
      onFocus(items[ki], x, y);
    });
    plot.addEventListener("blur", onBlur);
  }

  function rampFor(c) {
    const dark = root.getAttribute("data-mpc-theme") === "dark" || (root.getAttribute("data-mpc-theme") === "auto" && globalThis.matchMedia && matchMedia("(prefers-color-scheme: dark)").matches);
    const hm = c.heatmap;
    if (hm.scale === "diverging") {
      const neg = dark ? ["#f09a99", "#e34948", "#a83a3a", "#6b2626"] : ["#a8302f", "#e34948", "#ec8a89", "#f5c3c2"];
      const pos = dark ? ["#1c4a7d", "#2a6bbd", "#3987e5", "#86b6ef"] : ["#b7d3f6", "#6da7ec", "#2a78d6", "#184f95"];
      const mid = dark ? "#383835" : "#f0efec";
      const lo = hm.domain_min, hi = hm.domain_max, m = hm.midpoint ?? 0;
      return (v) => {
        if (v === m) return mid;
        if (v < m) { const f = Math.min(1, (m - v) / Math.max(1e-9, m - lo)); return neg[Math.min(3, 3 - Math.floor(f * 3.999))]; }
        const f = Math.min(1, (v - m) / Math.max(1e-9, hi - m)); return pos[Math.min(3, Math.floor(f * 3.999))];
      };
    }
    const steps = dark ? ["#0d366b", "#104281", "#1c5cab", "#2a78d6", "#5598e7", "#86b6ef", "#b7d3f6"]
                       : ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95", "#0d366b"];
    const lo = hm.domain_min, hi = hm.domain_max;
    const fn = (v) => steps[Math.max(0, Math.min(steps.length - 1, Math.floor(((v - lo) / Math.max(1e-9, hi - lo)) * (steps.length - 0.001))))];
    fn.steps = steps;
    return fn;
  }
  const inkOn = (hex) => { const n = parseInt(hex.slice(1), 16), r = n >> 16, g = (n >> 8) & 255, b = n & 255; return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? "#14201a" : "#ffffff"; };

  function drawSpecial(plot) {
    const c = state.view;
    const W = Math.max(280, plot.clientWidth || container.clientWidth || 600);
    const H = HEIGHTS[c.layout && c.layout.height] || 320;
    const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, height: H }, plot);
    const tip = makeTip(plot, c);
    const surf = css("--mpc-surface"), fgC = css("--mpc-fg"), fg2 = css("--mpc-fg-2"), mutedC = css("--mpc-muted"), gridC = css("--mpc-grid"), axisC = css("--mpc-axis");
    const fy = c.y_axis.format;

    if (c.type === "donut") {
      const d = c.donut, rows = c.data;
      const cx = W / 2, cy = H / 2, R = Math.min(H / 2 - 12, W / 2 - 12), r0 = R * 0.62;
      const total = rows.reduce((a, r) => a + (r[d.value_key] || 0), 0) || 1;
      let a0 = -Math.PI / 2;
      const items = [];
      const gap = Math.min(0.02, 2 / R);                  // 2px surface gap between slices
      rows.forEach((r, i) => {
        const frac = (r[d.value_key] || 0) / total, a1 = a0 + frac * 2 * Math.PI;
        const s = a0 + gap / 2, e = Math.max(s + 0.001, a1 - gap / 2), large = e - s > Math.PI ? 1 : 0;
        const P = (rad, ang) => [cx + rad * Math.cos(ang), cy + rad * Math.sin(ang)];
        const [x1, y1] = P(R, s), [x2, y2] = P(R, e), [x3, y3] = P(r0, e), [x4, y4] = P(r0, s);
        const col = sliceColor(r);
        const path = svgEl("path", { d: `M${x1},${y1}A${R},${R} 0 ${large} 1 ${x2},${y2}L${x3},${y3}A${r0},${r0} 0 ${large} 0 ${x4},${y4}Z`, fill: col }, svg);
        const mid = (s + e) / 2, [lx, ly] = P((R + r0) / 2, mid);
        items.push({ i, x: lx, y: ly, el: path, col, a0: s, a1: e });
        a0 = a1;
      });
      svgEl("text", { x: cx, y: cy - 4, "text-anchor": "middle", "font-size": Math.max(14, Math.min(24, r0 / 3.2)), "font-weight": 700, fill: fgC }, svg).textContent =
        formatValue(d.center_value, d.center_format || fy);
      svgEl("text", { x: cx, y: cy + 16, "text-anchor": "middle", "font-size": 12, fill: mutedC }, svg).textContent = d.center_label || "";
      const focus = (it, x, y) => { items.forEach((o) => (o === it ? o.el.removeAttribute("opacity") : o.el.setAttribute("opacity", 0.45))); tip.show(rows[it.i], x, y, it.col); };
      const blur = () => { items.forEach((o) => o.el.removeAttribute("opacity")); tip.hide(); };
      wireItems(plot, svg, W, items, focus, blur, (px, py) => {
        const dx = px - cx, dy = py - cy, dist = Math.hypot(dx, dy);
        if (dist < r0 - 6 || dist > R + 8) return null;
        let ang = Math.atan2(dy, dx); if (ang < -Math.PI / 2) ang += 2 * Math.PI;
        return items.find((o) => ang >= o.a0 - 0.02 && ang <= o.a1 + 0.02) || null;
      });
      return;
    }

    if (c.type === "scatter") {
      const sp = c.scatter, rows = c.data, yk = c.series[0].key, xk = c.x_axis.value_key, fx = c.x_axis.format;
      const vis = rows.filter((r) => !sp.group_key || !state.hidden.has(String(r[sp.group_key])));
      const m = { t: 16, r: 16, b: 54, l: 72 };
      const pw = W - m.l - m.r, ph = H - m.t - m.b;
      const dom = (vals, min, max, refs) => {
        const v = vals.concat(refs).filter((x) => typeof x === "number");
        let lo = Math.min(...v), hi = Math.max(...v); const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.05 || 1;
        lo = min ?? lo - pad; hi = max ?? hi + pad; return niceScale(lo, hi);
      };
      const refs = c.reference_lines || [];
      const sx = dom(rows.map((r) => r[xk]), c.x_axis.min, c.x_axis.max, refs.filter((l) => l.axis === "x").map((l) => l.value));
      const sy = dom(rows.map((r) => r[yk]), c.y_axis.min, c.y_axis.max, refs.filter((l) => l.axis === "y").map((l) => l.value));
      const X = (v) => m.l + ((v - sx.min) / (sx.max - sx.min)) * pw, Y = (v) => m.t + ph - ((v - sy.min) / (sy.max - sy.min)) * ph;
      sy.ticks.forEach((t) => { if (c.layout?.show_grid !== false) svgEl("line", { x1: m.l, x2: m.l + pw, y1: Y(t), y2: Y(t), stroke: gridC }, svg);
        svgEl("text", { x: m.l - 8, y: Y(t) + 4, "text-anchor": "end", "font-size": 11, fill: mutedC }, svg).textContent = axisLabel(t, fy); });
      sx.ticks.forEach((t) => { if (c.layout?.show_grid !== false) svgEl("line", { x1: X(t), x2: X(t), y1: m.t, y2: m.t + ph, stroke: gridC }, svg);
        svgEl("text", { x: X(t), y: m.t + ph + 16, "text-anchor": "middle", "font-size": 11, fill: mutedC }, svg).textContent = axisLabel(t, fx); });
      svgEl("line", { x1: m.l, x2: m.l + pw, y1: m.t + ph, y2: m.t + ph, stroke: axisC }, svg);
      svgEl("line", { x1: m.l, x2: m.l, y1: m.t, y2: m.t + ph, stroke: axisC }, svg);
      axisTitle(svg, 13, m.t + ph / 2, c.y_axis.title + (fy.unit ? ` (${fy.unit})` : ""), true);
      axisTitle(svg, m.l + pw / 2, H - 4, (c.x_axis.title || "") + (fx.unit ? ` (${fx.unit})` : ""), false);
      refs.forEach((l) => {
        const dash = l.line_style === "dashed" ? "5 4" : l.line_style === "dotted" ? "2 3" : "";
        if (l.axis === "x") { svgEl("line", { x1: X(l.value), x2: X(l.value), y1: m.t, y2: m.t + ph, stroke: css("--mpc-plan"), "stroke-width": 1.5, "stroke-dasharray": dash }, svg);
          svgEl("text", { x: X(l.value) + 4, y: m.t + 10, "font-size": 11, fill: fg2 }, svg).textContent = l.label; }
        else { svgEl("line", { x1: m.l, x2: m.l + pw, y1: Y(l.value), y2: Y(l.value), stroke: css("--mpc-plan"), "stroke-width": 1.5, "stroke-dasharray": dash }, svg);
          svgEl("text", { x: m.l + pw, y: Y(l.value) - 6, "text-anchor": "end", "font-size": 11, fill: fg2 }, svg).textContent = l.label; }
      });
      const items = vis.map((r) => {
        const col = groupColor(r), x = X(r[xk]), y = Y(r[yk]);
        const el = svgEl("circle", { cx: x, cy: y, r: 5, fill: col, stroke: surf, "stroke-width": 2 }, svg);
        return { i: rows.indexOf(r), x, y, el, col };
      });
      const ring = svgEl("circle", { r: 9, fill: "none", stroke: fgC, "stroke-width": 1.5, opacity: 0, "pointer-events": "none" }, svg);
      const focus = (it, x, y) => { ring.setAttribute("cx", it.x); ring.setAttribute("cy", it.y); ring.setAttribute("opacity", 1); tip.show(rows[it.i], x, y, it.col); };
      const blur = () => { ring.setAttribute("opacity", 0); tip.hide(); };
      wireItems(plot, svg, W, items.slice().sort((a, b) => a.x - b.x), focus, blur, (px, py) => {   // nearest point within 24px
        let best = null, bd = 24;
        items.forEach((o) => { const dd = Math.hypot(o.x - px, o.y - py); if (dd < bd) { bd = dd; best = o; } });
        return best;
      });
      return;
    }

    if (c.type === "heatmap") {
      const hm = c.heatmap, color = rampFor(c), vk = hm.value_key;
      const longest = Math.max(...hm.rows.map((r) => String(r.label).length));
      const m = { t: 10, r: 10, b: c.x_axis.title ? 50 : 34, l: Math.min(150, 14 + longest * 7) + (hm.row_title ? 18 : 0) };
      const pw = W - m.l - m.r, ph = H - m.t - m.b;
      const cw = pw / hm.cols.length, ch = ph / hm.rows.length;
      const cell = {}; c.data.forEach((r, i) => (cell[r[hm.row_key] + "\u0000" + r[hm.col_key]] = i));
      const items = [];
      hm.rows.forEach((rw, ri) => {
        svgEl("text", { x: m.l - 8, y: m.t + ri * ch + ch / 2 + 4, "text-anchor": "end", "font-size": 12, fill: fg2 }, svg).textContent = rw.label;
        hm.cols.forEach((cl, ci) => {
          const i = cell[rw.value + "\u0000" + cl.value];
          const x = m.l + ci * cw, y = m.t + ri * ch, row = i === undefined ? null : c.data[i];
          const v = row ? row[vk] : null;
          const fill = typeof v === "number" ? color(v) : css("--mpc-raise");
          const el = svgEl("rect", { x: x + 1, y: y + 1, width: Math.max(0, cw - 2), height: Math.max(0, ch - 2), rx: 2, fill }, svg);
          if (typeof v !== "number") svgEl("line", { x1: x + 4, y1: y + ch - 4, x2: x + cw - 4, y2: y + 4, stroke: css("--mpc-border"), "stroke-width": 1 }, svg);
          else if (c.data_labels.mode === "all" && cw > 34 && ch > 18)
            svgEl("text", { x: x + cw / 2, y: y + ch / 2 + 4, "text-anchor": "middle", "font-size": 11, "font-weight": 600, fill: inkOn(fill), "pointer-events": "none" }, svg).textContent = formatValue(v, { ...fy, unit: "" });
          if (row) items.push({ i, x: x + cw / 2, y: y + ch / 2, el });
        });
      });
      const every = Math.max(1, Math.ceil(hm.cols.length / Math.max(1, Math.floor(pw / (Math.max(...hm.cols.map((k) => String(k.label).length)) * 6.4 + 10)))));
      hm.cols.forEach((cl, ci) => { if (ci % every === 0) svgEl("text", { x: m.l + ci * cw + cw / 2, y: m.t + ph + 16, "text-anchor": "middle", "font-size": 11, fill: fg2 }, svg).textContent = cl.label; });
      if (c.x_axis.title) axisTitle(svg, m.l + pw / 2, H - 4, c.x_axis.title, false);
      if (hm.row_title) axisTitle(svg, 13, m.t + ph / 2, hm.row_title, true);
      const outline = svgEl("rect", { fill: "none", stroke: fgC, "stroke-width": 2, rx: 3, opacity: 0, "pointer-events": "none" }, svg);
      const focus = (it, x, y) => { outline.setAttribute("x", it.x - cw / 2 + 0.5); outline.setAttribute("y", it.y - ch / 2 + 0.5); outline.setAttribute("width", cw - 1); outline.setAttribute("height", ch - 1); outline.setAttribute("opacity", 1); tip.show(c.data[it.i], x, y, null); };
      const blur = () => { outline.setAttribute("opacity", 0); tip.hide(); };
      wireItems(plot, svg, W, items, focus, blur, (px, py) => {
        const ci = Math.floor((px - m.l) / cw), ri = Math.floor((py - m.t) / ch);
        if (ci < 0 || ri < 0 || ci >= hm.cols.length || ri >= hm.rows.length) return null;
        const i = cell[hm.rows[ri].value + "\u0000" + hm.cols[ci].value];
        return i === undefined ? null : items.find((o) => o.i === i);
      });
    }
  }

  const sliceColor = (r) => { const k = r.color_index; return !k ? css("--mpc-plan") : css("--mpc-cat" + Math.min(8, k)); };
  const groupColor = (r) => {
    const sp = state.view.scatter;
    if (!sp.group_key) return css("--mpc-actual");
    const g = (sp.groups || []).find((x) => String(x.value) === String(r[sp.group_key]));
    return g ? css("--mpc-cat" + Math.min(8, g.color_index || 1)) : css("--mpc-plan");
  };

  function drawSpecialLegend(legend) {
    const c = state.view;
    legend.replaceChildren();
    legend.hidden = !(c.legend && c.legend.show);
    if (legend.hidden) return;
    if (c.type === "donut") {
      c.data.forEach((r) => {
        const b = h("button"); b.type = "button"; b.disabled = true;
        const k = h("span", "mpc-key"); k.style.background = sliceColor(r);
        b.append(k, doc.createTextNode(String(r[c.x_axis.label_key])));
        const sk = c.donut.share_key; if (sk && typeof r[sk] === "number") b.appendChild(h("span", "mpc-legval", formatValue(r[sk], { decimals: 1, thousands_separator: true, unit: "%", unit_position: "suffix" })));
        legend.appendChild(b);
      });
    } else if (c.type === "scatter" && c.scatter.group_key) {
      const sp = c.scatter;
      (sp.groups || []).forEach((g) => {
        const b = h("button"); b.type = "button";
        const key = String(g.value);
        b.setAttribute("aria-pressed", String(!state.hidden.has(key)));
        const k = h("span", "mpc-key mpc-round"); k.style.background = css("--mpc-cat" + Math.min(8, g.color_index || 1));
        b.append(k, doc.createTextNode(g.name));
        if (c.interaction && c.interaction.series_toggle) b.onclick = () => {
          if (state.hidden.has(key)) state.hidden.delete(key); else if (sp.groups.length - state.hidden.size > 1) state.hidden.add(key);
          build();
        }; else b.disabled = true;
        legend.appendChild(b);
      });
    } else if (c.type === "heatmap") {
      const hm = c.heatmap, color = rampFor(c), fy = c.y_axis.format;
      const wrap = h("div", "mpc-heatkey");
      wrap.appendChild(h("span", null, c.y_axis.title));
      wrap.appendChild(h("span", "mpc-heatnum", formatValue(hm.domain_min, fy)));
      const bar = h("span", "mpc-heatbar");
      for (let k = 0; k < 7; k++) { const v = hm.domain_min + ((hm.domain_max - hm.domain_min) * (k + 0.5)) / 7; const sw = h("i"); sw.style.background = color(v); bar.appendChild(sw); }
      wrap.appendChild(bar);
      wrap.appendChild(h("span", "mpc-heatnum", formatValue(hm.domain_max, fy)));
      if (c.data.length < hm.rows.length * hm.cols.length || c.data.some((r) => typeof r[hm.value_key] !== "number")) {
        const nd = h("span", "mpc-st"); const sw = h("i", "mpc-nodata"); nd.append(sw, h("span", null, hm.null_label || "No data")); wrap.appendChild(nd);
      }
      legend.appendChild(wrap);
    } else legend.hidden = true;
  }

  function drawHeatTable(wrap) {
    const c = state.chart, hm = c.heatmap, fy = c.y_axis.format;
    const t = h("table"), head = t.createTHead().insertRow(), body = t.createTBody();
    head.appendChild(h("th", null, hm.row_title || ""));
    hm.cols.forEach((cl) => head.appendChild(h("th", null, cl.label)));
    const cell = {}; c.data.forEach((r) => (cell[r[hm.row_key] + "\u0000" + r[hm.col_key]] = r));
    hm.rows.forEach((rw) => {
      const tr = body.insertRow(); tr.appendChild(h("td", null, rw.label));
      hm.cols.forEach((cl) => { const r = cell[rw.value + "\u0000" + cl.value]; tr.appendChild(h("td", null, r ? formatValue(r[hm.value_key], fy) : "—")); });
    });
    wrap.replaceChildren(t);
  }

  async function load(chart) {
    const token = ++state.token;
    const problems = validateChart(chart);
    if (problems.length) return fail(problems);
    if (opts.verify) {
      const ok = await verifyChecksum(chart).catch(() => null);
      if (token !== state.token || state.destroyed) return;
      if (ok === false) return fail(["integrity.checksum does not match data"]);
      if (ok === null) console.warn("[mineprod-chart] checksum not verified (crypto.subtle unavailable; use https or localhost)");
    }
    state.chart = chart;
    state.view = chart;
    state.hidden.clear();
    build();
  }

  // redraw on resize and on OS theme change
  if (typeof ResizeObserver !== "undefined") {
    let last = 0, raf = 0;
    state.ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      if (!state.chart || Math.abs(w - last) < 2) return;
      last = w; cancelAnimationFrame(raf); raf = requestAnimationFrame(() => !state.destroyed && build());
    });
    state.ro.observe(container);
  }
  if (globalThis.matchMedia) {
    state.mq = matchMedia("(prefers-color-scheme: dark)");
    state.mqHandler = () => { if (state.chart && root.getAttribute("data-mpc-theme") === "auto") build(); };
    state.mq.addEventListener("change", state.mqHandler);
  }

  load(chart);

  return {
    update(next) { return load(next); },
    setTheme(theme) { root.setAttribute("data-mpc-theme", theme); if (state.chart) build(); },
    copyImage() { return state.chart ? copyImage() : Promise.resolve("failed"); },
    copyData() { return state.chart ? copyData() : Promise.resolve("failed"); },
    downloadPng() { return state.chart ? downloadPng() : Promise.resolve("failed"); },
    toPngBlob() { return state.chart ? toPngBlob() : Promise.reject(new Error("no chart")); },
    setType(type) { if (state.chart && compatibleTypes(state.chart).includes(type)) { state.view = viewAs(state.chart, type); state.hidden.clear(); build(); } },
    destroy() {
      state.destroyed = true;
      if (closeMenu) closeMenu();
      if (state.ro) state.ro.disconnect();
      if (state.mq) state.mq.removeEventListener("change", state.mqHandler);
      container.replaceChildren();
    },
  };
}

/** Pulls a ```mineprod-chart fenced block out of an agent reply. Returns { chart, text } (chart null if none or invalid JSON). */
export function extractChartFromText(text) {
  const re = /```mineprod-chart\s*\n([\s\S]*?)\n```/;
  const m = typeof text === "string" ? text.match(re) : null;
  if (!m) return { chart: null, text, before: typeof text === "string" ? text : "", after: "" };
  let chart = null;
  try { chart = JSON.parse(m[1]); } catch { chart = null; }
  const before = text.slice(0, m.index).replace(/\n{3,}/g, "\n\n").trim();
  const after = text.slice(m.index + m[0].length).replace(/\n{3,}/g, "\n\n").trim();
  return { chart, before, after, text: [before, after].filter(Boolean).join("\n\n") };
}

/** Option A helper: splits a trailing follow-up question off the answer, so the UI can draw the chart between them.
 *  Returns { body, followUp } (followUp "" when the last paragraph is not a question). */
export function splitFollowUp(text) {
  if (typeof text !== "string") return { body: "", followUp: "" };
  const parts = text.trim().split(/\n{2,}/);
  const last = parts[parts.length - 1] || "";
  if (parts.length > 1 && /\?\s*$/.test(last) && last.length <= 300) return { body: parts.slice(0, -1).join("\n\n"), followUp: last.trim() };
  return { body: text.trim(), followUp: "" };
}
